import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { fetchMemberDashboard } from '../memberDashboardApi';
import { submitMemberDecisionVote } from '../memberDecisionsApi';
import { useT } from '../i18n/translations';
import './Dashboard.css';
import './MemberDashboard.css';

function getVoteLabels(t) {
  return {
    approve: { label: t('member.voteApprove'), cls: 'status-badge--approved', iconName: 'check-circle', btnCls: 'vote-btn--approve', selectedCls: 'vote-btn--selected-approve', color: '#02c3a6' },
    reject:  { label: t('member.voteReject'),  cls: 'status-badge--rejected', iconName: 'x-circle',     btnCls: 'vote-btn--reject',  selectedCls: 'vote-btn--selected-reject',  color: '#c62828' },
    abstain: { label: t('member.voteAbstain'), cls: 'status-badge--pending',  iconName: 'clock',        btnCls: 'vote-btn--abstain', selectedCls: 'vote-btn--selected-abstain', color: '#546e7a' },
  };
}

function VoteCard({ decision, onVote, t, voteLabels }) {
  const [selected, setSelected]   = useState(decision.myVote);
  const [note, setNote]           = useState(decision.myNote);
  const [submitted, setSubmitted] = useState(!!decision.myVote);
  const [saving, setSaving]       = useState(false);

  const total = decision.votes.total;
  const pct   = (n) => total ? Math.round((n / total) * 100) : 0;

  const handleSubmit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onVote(decision.id, selected, note);
      setSaving(false);
      setSubmitted(true);
    } catch {
      setSaving(false);
    }
  };

  const daysLeft = Math.ceil((new Date(decision.deadline) - new Date()) / 86400000);

  return (
    <div className="vote-card">
      <div className="vote-card__header">
        <div className="vote-card__branch">
          <Icon name="branch" size={12} color="#78909c" />
          {decision.branch}
          <span style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="clock" size={12} color={daysLeft <= 2 ? '#e53935' : '#f57c00'} />
            <span style={{ color: daysLeft <= 2 ? '#e53935' : '#f57c00', fontSize: '0.7rem', fontWeight: 700 }}>
              {daysLeft > 0 ? `${daysLeft} ${t('member.daysLeft')}` : t('member.deadlineEnded')}
            </span>
          </span>
        </div>
        <div className="vote-card__title">{decision.title}</div>
      </div>

      <div className="vote-card__desc">{decision.description}</div>

      <div className="vote-card__meta">
        <span className="vote-card__meta-item"><Icon name="calendar" size={11} />{decision.date}</span>
        <span className="vote-card__meta-item"><Icon name="user" size={11} />{decision.votes.approve + decision.votes.reject + decision.votes.abstain} {t('member.votedCount')}</span>
        <span className="vote-card__meta-item"><Icon name="clock" size={11} />{t('member.until')} {decision.deadline}</span>
      </div>

      {/* Vote result mini bars */}
      <div className="vote-results">
        <div className="vote-result-row">
          <span className="vote-result-label">{voteLabels.approve.label}</span>
          <div className="vote-result-track"><div className="vote-result-fill vote-result-fill--approve" style={{ width: `${pct(decision.votes.approve)}%` }} /></div>
          <span className="vote-result-count">{decision.votes.approve}</span>
        </div>
        <div className="vote-result-row">
          <span className="vote-result-label">{voteLabels.reject.label}</span>
          <div className="vote-result-track"><div className="vote-result-fill vote-result-fill--reject"  style={{ width: `${pct(decision.votes.reject)}%` }} /></div>
          <span className="vote-result-count">{decision.votes.reject}</span>
        </div>
        <div className="vote-result-row">
          <span className="vote-result-label">{voteLabels.abstain.label}</span>
          <div className="vote-result-track"><div className="vote-result-fill vote-result-fill--abstain" style={{ width: `${pct(decision.votes.abstain)}%` }} /></div>
          <span className="vote-result-count">{decision.votes.abstain}</span>
        </div>
      </div>

      {submitted ? (
        <div className="vote-card__voted">
          <Icon name="check-circle" size={16} color="#02c3a6" />
          {t('member.youVoted')}
          <span className={`status-badge ${voteLabels[selected].cls}`} style={{ marginRight: 4 }}>
            <Icon name={voteLabels[selected].iconName} size={11} /> {voteLabels[selected].label}
          </span>
          {note && <span style={{ color: '#90a4ae', fontSize: '0.75rem', marginRight: 4 }}>"{note}"</span>}
        </div>
      ) : (
        <>
          <div className="vote-card__actions">
            {['approve', 'reject', 'abstain'].map((v) => {
              const vl = voteLabels[v];
              const isSelected = selected === v;
              return (
                <button
                  key={v}
                  className={`vote-btn ${vl.btnCls}${isSelected ? ` ${vl.selectedCls}` : ''}`}
                  onClick={() => setSelected(isSelected ? null : v)}
                >
                  <Icon name={vl.iconName} size={13} color={isSelected ? '#fff' : vl.color} />
                  {vl.label}
                </button>
              );
            })}
          </div>
          {selected && (
            <div className="vote-card__note-wrap">
              <textarea
                className="vote-card__note"
                rows={2}
                placeholder={t('member.addNote')}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button className="vote-card__submit" onClick={handleSubmit} disabled={saving}>
                {saving
                  ? <>{t('member.sendingVote')} <span style={{ display:'inline-block', width:13, height:13, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /></>
                  : <><Icon name="check" size={14} color="#fff" /> {t('member.submitVote')}</>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MemberDashboard() {
  const t = useT();
  const voteLabels = getVoteLabels(t);
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [history, setHistory] = useState([]);
  const [branchName, setBranchName] = useState('—');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const payload = await fetchMemberDashboard();
        if (cancelled) return;
        setBranchName(payload.branch || '—');
        const mapped = (payload.decisions ?? []).map((d) => {
          const voteCounts = { approve: 0, reject: 0, abstain: 0 };
          (d.votes ?? []).forEach((v) => {
            if (v.vote === 'approve') voteCounts.approve += 1;
            else if (v.vote === 'reject') voteCounts.reject += 1;
            else voteCounts.abstain += 1;
          });
          return {
            ...d,
            votes: { ...voteCounts, total: d.totalMembers ?? 0 },
          };
        });
        setDecisions(mapped);
        setHistory(payload.history ?? []);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = async (id, vote, note) => {
    const updated = await submitMemberDecisionVote(id, { choice: vote, notes: note });
    const voteCounts = { approve: 0, reject: 0, abstain: 0 };
    (updated.votes ?? []).forEach((v) => {
      if (v.vote === 'approve') voteCounts.approve += 1;
      else if (v.vote === 'reject') voteCounts.reject += 1;
      else voteCounts.abstain += 1;
    });
    setDecisions((prev) => prev.map((d) => d.id === id ? {
      ...d,
      ...updated,
      votes: { ...voteCounts, total: updated.totalMembers ?? d.votes.total },
    } : d));
  };

  const pending  = decisions.filter((d) => !d.myVote).length;
  const approved = decisions.filter((d) => d.myVote === 'approve').length;
  const rejected = decisions.filter((d) => d.myVote === 'reject').length;
  const abstained = decisions.filter((d) => d.myVote === 'abstain').length;

  const histVoteCounts = {
    approve: history.filter((h) => h.myVote === 'approve').length,
    reject:  history.filter((h) => h.myVote === 'reject').length,
    abstain: history.filter((h) => h.myVote === 'abstain').length,
  };

  return (
    <div className="mdash">
      {loadError && <div style={{ marginBottom: 12, padding: '10px 14px', background: '#ffebee', color: '#c62828', borderRadius: 10 }}>{loadError}</div>}
      {loading && <div style={{ marginBottom: 12, color: '#78909c' }}>{t('member.loading')}</div>}

      {/* Welcome banner */}
      <div className="mdash__welcome">
        <div className="mdash__welcome-left">
          <div className="mdash__welcome-avatar">BM</div>
          <div className="mdash__welcome-text">
            <h2>{t('member.welcome')}</h2>
            <p>{t('member.pendingMsg')} {pending} {t('member.pendingTail')}</p>
          </div>
        </div>
        <div className="mdash__welcome-meta">
          <div className="mdash__welcome-chip">
            <Icon name="branch" size={14} color="#fff" />
            {branchName}
          </div>
          <div className="mdash__welcome-chip">
            <Icon name="board" size={14} color="#fff" />
            {t('member.role')}
          </div>
          <div className="mdash__welcome-chip" style={{ background: pending ? 'rgba(251,192,45,0.25)' : 'rgba(255,255,255,0.14)', borderColor: pending ? 'rgba(251,192,45,0.5)' : 'rgba(255,255,255,0.22)' }}>
            <Icon name="clock" size={14} color={pending ? '#fbc02d' : '#fff'} />
            {pending} {t('member.inVoting')}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mdash__stats">
        <div className="stat-card stat-card--purple">
          <div className="stat-card__top">
            <div className="stat-card__icon-wrap"><Icon name="decisions" size={22} color="#02c3a6" /></div>
            <div className="stat-card__trend stat-card__trend--up">▲ {t('member.active')}</div>
          </div>
          <div className="stat-card__value">{decisions.length + history.length}</div>
          <div className="stat-card__label">{t('member.totalDecisions')}</div>
          <div className="stat-card__sub">{t('member.currentCycle')}</div>
        </div>

        <div className="stat-card stat-card--orange">
          <div className="stat-card__top">
            <div className="stat-card__icon-wrap"><Icon name="clock" size={22} color="#e65100" /></div>
          </div>
          <div className="stat-card__value">{pending}</div>
          <div className="stat-card__label">{t('member.inVoting')}</div>
          <div className="stat-card__sub">{t('member.waitingOpinion')}</div>
        </div>

        <div className="stat-card stat-card--green">
          <div className="stat-card__top">
            <div className="stat-card__icon-wrap"><Icon name="check-circle" size={22} color="#02c3a6" /></div>
            <div className="stat-card__trend stat-card__trend--up">▲</div>
          </div>
          <div className="stat-card__value">{approved + histVoteCounts.approve}</div>
          <div className="stat-card__label">{t('member.votedApprove')}</div>
          <div className="stat-card__sub">{t('member.ofVotes')}</div>
        </div>

        <div className="stat-card stat-card--blue">
          <div className="stat-card__top">
            <div className="stat-card__icon-wrap"><Icon name="x-circle" size={22} color="#e53935" /></div>
          </div>
          <div className="stat-card__value">{rejected + histVoteCounts.reject}</div>
          <div className="stat-card__label">{t('member.votedReject')}</div>
          <div className="stat-card__sub">{t('member.abstained')}: {abstained + histVoteCounts.abstain}</div>
        </div>
      </div>

      {/* Pending decisions */}
      <div className="mdash__section-header">
        <div className="mdash__section-title">
          <Icon name="clock" size={17} color="#f57c00" />
          {t('member.pendingSection')}
          <span className="mdash__section-badge">{pending} {t('member.decisionUnit')}</span>
        </div>
        <span className="mdash__view-all" onClick={() => navigate('/member/decisions')}>
          {t('dashboard.viewAll')} <Icon name="chevron-left" size={12} />
        </span>
      </div>

      {pending === 0 ? (
        <div className="table-card" style={{ borderRadius: 16, border: '1px solid #e8eef6', marginBottom: 28 }}>
          <div className="empty-state">
            <div className="empty-state__icon"><Icon name="check-circle" size={48} color="#a5d6a7" strokeWidth={1.2} /></div>
            <div className="empty-state__title" style={{ color: '#02c3a6' }}>{t('member.allVotedTitle')}</div>
            <div className="empty-state__sub">{t('member.allVotedSub')}</div>
          </div>
        </div>
      ) : (
        <div className="mdash__pending-grid">
          {decisions.filter((d) => !d.myVote).map((d) => (
            <VoteCard key={d.id} decision={d} onVote={handleVote} t={t} voteLabels={voteLabels} />
          ))}
        </div>
      )}

      {/* Voted decisions — show voted ones compactly */}
      {decisions.some((d) => d.myVote) && (
        <>
          <div className="mdash__section-header">
            <div className="mdash__section-title">
              <Icon name="check-circle" size={17} color="#02c3a6" />
              {t('member.votedThisSession')}
            </div>
          </div>
          <div className="mdash__pending-grid">
            {decisions.filter((d) => d.myVote).map((d) => (
              <VoteCard key={d.id} decision={d} onVote={handleVote} t={t} voteLabels={voteLabels} />
            ))}
          </div>
        </>
      )}

      {/* Vote history table */}
      <div className="mdash__section-header">
        <div className="mdash__section-title">
          <Icon name="report" size={17} color="#02c3a6" />
          {t('member.historyTitle')}
        </div>
        <span className="mdash__view-all" onClick={() => navigate('/member/history')}>
          {t('dashboard.viewAll')} <Icon name="chevron-left" size={12} />
        </span>
      </div>

      <div className="mdash__history">
        <table className="dash__table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('member.colDecision')}</th>
              <th>{t('member.colBranch')}</th>
              <th>{t('member.colVoteDate')}</th>
              <th>{t('member.colMyVote')}</th>
              <th>{t('member.colMyNote')}</th>
            </tr>
          </thead>
          <tbody>
            {history.map((row, i) => {
              const v = voteLabels[row.myVote];
              return (
                <tr key={row.id}>
                  <td style={{ color: '#90a4ae', fontSize: '0.78rem', width: 36 }}>{i + 1}</td>
                  <td><strong style={{ color: '#00334a', fontSize: '0.86rem' }}>{row.title}</strong></td>
                  <td>{row.branch}</td>
                  <td>{row.date}</td>
                  <td>
                    <span className={`status-badge ${v.cls}`}>
                      <Icon name={v.iconName} size={11} /> {v.label}
                    </span>
                  </td>
                  <td style={{ color: '#78909c', fontSize: '0.8rem', fontStyle: row.note ? 'normal' : 'italic' }}>
                    {row.note || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}

export default MemberDashboard;
